import * as THREE from 'three';
import { TransformControls } from './TransformControls.js';
import {
  ensureUserDataExtensible,
  setUserDataField,
} from './syncObjectTransform.js';

/**
 * Pivot-point editor.
 *
 * Right-click → Edit Pivot Point places a draggable marker at the object's
 * origin. TransformControls move the marker; Enter applies (geometry stays
 * put, origin moves); Escape cancels.
 */

const PIVOT_MARKER_NAME = '__pivotEditorMarker';
const PIVOT_CONTROLS_NAME = '__pivotEditorControls';

let session = null;

function createPivotMarker() {
  const marker = new THREE.Group();
  marker.name = PIVOT_MARKER_NAME;
  marker.userData.isHelper = true;
  marker.userData.isPivotEditorMarker = true;
  marker.raycast = () => {};

  const sphere = new THREE.Mesh(
    new THREE.SphereGeometry(1, 24, 24),
    new THREE.MeshBasicMaterial({
      color: 0xff2222,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.95,
    })
  );
  sphere.renderOrder = 10000;
  sphere.raycast = () => {};
  marker.add(sphere);

  const axes = new THREE.AxesHelper(2.2);
  if (axes.material) {
    axes.material.depthTest = false;
    axes.material.transparent = true;
  }
  axes.renderOrder = 10000;
  axes.raycast = () => {};
  marker.add(axes);

  return marker;
}

function computeMarkerScale(object) {
  try {
    const box = new THREE.Box3().setFromObject(object);
    if (box.isEmpty()) return 0.1;
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    return Math.max(maxDim * 0.04, 0.03);
  } catch (_) {
    return 0.1;
  }
}

function disposeObject3D(root) {
  root?.traverse?.((obj) => {
    obj.geometry?.dispose?.();
    if (Array.isArray(obj.material)) {
      obj.material.forEach((m) => m?.dispose?.());
    } else {
      obj.material?.dispose?.();
    }
  });
}

const PIVOT_EDITABLE_DELETE_TYPES = new Set([
  'mesh',
  'mesh-extracted',
  'image',
  'video',
  'audio',
  'text',
  'hotspot',
]);

/** Pivot editing for scene assets (meshes, media, text, hotspots). */
export function canEditPivot(object) {
  if (!object || !object.isObject3D) return false;
  if (object.userData?.isMultiSelectionPivot) return false;
  if (object.userData?.isPivotEditorMarker) return false;
  if (object.isLight || object.isCamera) return false;
  // Particles do not support Edit Pivot Point.
  if (
    object.deleteType === 'particle' ||
    object.userData?.isParticle ||
    object.userData?.particleType
  ) {
    return false;
  }
  if (
    typeof object.type === 'string' &&
    (object.type.includes('Light') ||
      object.type.includes('Helper') ||
      object.type === 'Camera' ||
      object.type === 'PerspectiveCamera' ||
      object.type === 'OrthographicCamera')
  ) {
    return false;
  }
  if (
    object.userData?.isSceneCamera ||
    object.userData?.isHdriBackground ||
    object.name === 'hdriBackgroundSphere' ||
    object.deleteType === 'Environment'
  ) {
    return false;
  }

  const glbType = object.glb_type ?? object.userData?.glb_type ?? '';
  const isShape = !!(
    object.isShapes ||
    object.userData?.isShapes ||
    glbType === 'shapes' ||
    object.mesh_code ||
    object.userData?.mesh_code
  );
  if (isShape) return true;

  if (
    object.deleteType &&
    PIVOT_EDITABLE_DELETE_TYPES.has(object.deleteType)
  ) {
    return true;
  }

  // Uploaded / library 3D assets without a clear deleteType yet
  return object.objType === '3d' || object.deleteType === 'mesh';
}

/** Re-apply a published/stored content offset after geometry is rebuilt. */
export function applyStoredPivotContentMatrix(object, matrixArray) {
  if (!object || !Array.isArray(matrixArray) || matrixArray.length !== 16) {
    return false;
  }
  const matrix = new THREE.Matrix4().fromArray(matrixArray);
  if (object.isMesh && object.geometry) {
    object.geometry.applyMatrix4(matrix);
    object.geometry.computeBoundingBox?.();
    object.geometry.computeBoundingSphere?.();
  } else if (object.isObject3D) {
    object.children.slice().forEach((child) => {
      if (
        child.userData?.isHelper ||
        child.userData?.isPivotEditorMarker ||
        child.isHelper
      ) {
        return;
      }
      child.applyMatrix4(matrix);
    });
  } else {
    return false;
  }
  ensureUserDataExtensible(object);
  setUserDataField(object, 'pivotContentMatrix', matrixArray.slice());
  setUserDataField(object, 'pivotEdited', true);
  setUserDataField(object, 'native_pivot', true);
  setUserDataField(object, 'pivot_mode', 'file');
  try {
    object.pivotEdited = true;
    object.native_pivot = true;
    object.pivot_mode = 'file';
  } catch (_) {
    /* flags also on userData */
  }
  return true;
}

/** Apply a simple local translation pivot offset (publish fallback). */
export function applyStoredPivotOffset(object, offset) {
  if (!object || !offset) return false;
  const x = Number(offset.x) || 0;
  const y = Number(offset.y) || 0;
  const z = Number(offset.z) || 0;
  if (x === 0 && y === 0 && z === 0) return false;

  if (object.isMesh && object.geometry) {
    object.geometry.translate(x, y, z);
    object.geometry.computeBoundingBox?.();
    object.geometry.computeBoundingSphere?.();
  } else if (object.isObject3D) {
    const translate = new THREE.Matrix4().makeTranslation(x, y, z);
    object.children.slice().forEach((child) => {
      if (
        child.userData?.isHelper ||
        child.userData?.isPivotEditorMarker ||
        child.isHelper
      ) {
        return;
      }
      child.applyMatrix4(translate);
    });
  } else {
    return false;
  }

  ensureUserDataExtensible(object);
  setUserDataField(object, 'pivotOffset', { x, y, z });
  setUserDataField(object, 'pivotEdited', true);
  setUserDataField(object, 'native_pivot', true);
  setUserDataField(object, 'pivot_mode', 'file');
  try {
    object.pivotEdited = true;
    object.native_pivot = true;
    object.pivot_mode = 'file';
  } catch (_) {
    /* flags also on userData */
  }
  return true;
}

const PIVOT_CACHE_KEY = 'spatialgrid_pivot_restore_v1';
export const PIVOT_RESTORE_EVENT_ACTION = '__pivot_restore__';

function readPivotCacheStore() {
  try {
    return JSON.parse(sessionStorage.getItem(PIVOT_CACHE_KEY) || '{}');
  } catch (_) {
    return {};
  }
}

function writePivotCacheStore(store) {
  try {
    sessionStorage.setItem(PIVOT_CACHE_KEY, JSON.stringify(store));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

/** Persist pivot restore payload for publish→reload (API may strip custom fields). */
export function cachePivotRestoreData(projectId, assetKeys, pivotFields) {
  if (!projectId || !pivotFields) return;
  const hasFilePivot =
    pivotFields.pivot_mode === 'file' || !!pivotFields.native_pivot;
  if (!pivotFields.pivot_edited && !hasFilePivot) return;
  const keys = (Array.isArray(assetKeys) ? assetKeys : [assetKeys]).filter(
    Boolean
  );
  if (!keys.length) return;
  const store = readPivotCacheStore();
  const payload = {
    ...(pivotFields.pivot_edited ? { pivot_edited: true } : {}),
    ...(hasFilePivot
      ? { native_pivot: true, pivot_mode: 'file' }
      : {}),
    pivot_offset: pivotFields.pivot_offset || null,
    pivot_content_matrix: pivotFields.pivot_content_matrix || null,
    savedAt: Date.now(),
  };
  keys.forEach((key) => {
    store[`${projectId}::${String(key)}`] = payload;
  });
  writePivotCacheStore(store);
}

export function readCachedPivotRestoreData(projectId, ...assetKeys) {
  if (!projectId) return null;
  const store = readPivotCacheStore();
  for (const key of assetKeys) {
    if (!key) continue;
    const hit = store[`${projectId}::${String(key)}`];
    if (hit?.pivot_edited || hit?.pivot_mode === 'file' || hit?.native_pivot) {
      return hit;
    }
  }
  return null;
}

export function embedPivotInEvents(events, pivotFields) {
  const list = Array.isArray(events) ? events.slice() : [];
  const filtered = list.filter(
    (e) => e?.action !== PIVOT_RESTORE_EVENT_ACTION
  );
  if (!pivotFields) return filtered;
  const hasFilePivot =
    pivotFields.pivot_mode === 'file' || !!pivotFields.native_pivot;
  if (!pivotFields.pivot_edited && !hasFilePivot) return filtered;
  filtered.push({
    action: PIVOT_RESTORE_EVENT_ACTION,
    ...(pivotFields.pivot_edited ? { pivot_edited: true } : {}),
    ...(hasFilePivot
      ? { native_pivot: true, pivot_mode: 'file' }
      : {}),
    ...(pivotFields.pivot_offset
      ? { pivot_offset: pivotFields.pivot_offset }
      : {}),
    ...(Array.isArray(pivotFields.pivot_content_matrix) &&
    pivotFields.pivot_content_matrix.length === 16
      ? { pivot_content_matrix: pivotFields.pivot_content_matrix }
      : {}),
  });
  return filtered;
}

export function extractPivotFromEvents(events) {
  if (!Array.isArray(events)) return null;
  const hit = events.find((e) => e?.action === PIVOT_RESTORE_EVENT_ACTION);
  if (!hit) return null;
  return {
    ...(hit.pivot_edited ? { pivot_edited: true } : {}),
    ...(hit.pivot_mode === 'file' || hit.native_pivot
      ? { native_pivot: true, pivot_mode: 'file' }
      : {}),
    pivot_offset: hit.pivot_offset || null,
    pivot_content_matrix: hit.pivot_content_matrix || null,
  };
}

/** Resolve pivot matrix/offset from asset JSON / nested settings / events / cache. */
export function resolvePivotRestoreData(source = {}, options = {}) {
  const { projectId = null, cacheKeys = [] } = options;
  const eventPivot = extractPivotFromEvents(
    source.events || source.Events || source.userData?.events
  );
  const cached = projectId
    ? readCachedPivotRestoreData(projectId, ...cacheKeys)
    : null;

  const bags = [
    eventPivot,
    cached,
    source,
    source.userData,
    source.material_settings,
    source.userData?.material_settings,
    source.video_settings,
    source.userData?.video_settings,
    source.audio_settings,
    source.userData?.audio_settings,
    source.text_material,
    source.userData?.text_material,
    source.userData?.hotspot,
  ].filter(Boolean);

  let matrix = null;
  let offset = null;
  let edited = false;

  for (const bag of bags) {
    if (
      !matrix &&
      Array.isArray(bag.pivot_content_matrix) &&
      bag.pivot_content_matrix.length === 16
    ) {
      matrix = bag.pivot_content_matrix;
    }
    if (
      !matrix &&
      Array.isArray(bag.pivotContentMatrix) &&
      bag.pivotContentMatrix.length === 16
    ) {
      matrix = bag.pivotContentMatrix;
    }
    if (!offset && bag.pivot_offset) offset = bag.pivot_offset;
    if (!offset && bag.pivotOffset) offset = bag.pivotOffset;
    if (bag.pivot_edited || bag.pivotEdited) edited = true;
  }

  if (matrix || offset) edited = true;
  return { matrix, offset, edited };
}

/**
 * Apply any stored pivot restore data onto a freshly created scene object.
 * Prefers full matrix; falls back to translation offset.
 */
export function applyStoredPivotToObject(object, source = {}, options = {}) {
  if (!object) return false;
  const { matrix, offset, edited } = resolvePivotRestoreData(source, options);
  if (matrix && applyStoredPivotContentMatrix(object, matrix)) return true;
  if (offset && applyStoredPivotOffset(object, offset)) return true;
  if (edited) {
    ensureUserDataExtensible(object);
    setUserDataField(object, 'pivotEdited', true);
    try {
      object.pivotEdited = true;
    } catch (_) {
      /* ignore */
    }
  }
  return false;
}

/** Nest pivot fields into a settings object that the API already persists. */
export function nestPivotFieldsInSettings(settings, pivotFields) {
  const base =
    settings && typeof settings === 'object' ? { ...settings } : {};
  if (!pivotFields) return base;

  const hasFilePivot =
    pivotFields.pivot_mode === 'file' || !!pivotFields.native_pivot;
  if (!pivotFields.pivot_edited && !hasFilePivot) return base;

  return {
    ...base,
    ...(pivotFields.pivot_edited ? { pivot_edited: true } : {}),
    ...(hasFilePivot
      ? { native_pivot: true, pivot_mode: 'file' }
      : pivotFields.pivot_mode
        ? { pivot_mode: pivotFields.pivot_mode }
        : {}),
    ...(pivotFields.pivot_offset
      ? { pivot_offset: pivotFields.pivot_offset }
      : {}),
    ...(Array.isArray(pivotFields.pivot_content_matrix) &&
    pivotFields.pivot_content_matrix.length === 16
      ? { pivot_content_matrix: pivotFields.pivot_content_matrix }
      : {}),
  };
}

/** Fields to include on publish payloads so reloads keep the edited pivot. */
export function getPivotPublishFields(object) {
  const pivotEdited = !!(
    object?.userData?.pivotEdited ||
    object?.pivotEdited ||
    object?.pivot_edited
  );
  const matrix =
    object?.userData?.pivotContentMatrix || object?.pivot_content_matrix;
  const offset = object?.userData?.pivotOffset || object?.pivot_offset;
  let pivotMode =
    object?.userData?.pivot_mode || object?.pivot_mode || null;

  // Skinned assets default to file pivot on publish so reconstruct keeps feet origin.
  if (!pivotMode && object?.traverse) {
    let skinned = false;
    object.traverse((o) => {
      if (o?.isSkinnedMesh) skinned = true;
    });
    if (skinned) pivotMode = 'file';
  }

  const nativePivot = !!(
    object?.userData?.native_pivot ||
    object?.native_pivot ||
    pivotMode === 'file'
  );
  return {
    ...(nativePivot ? { native_pivot: true } : {}),
    ...(pivotMode ? { pivot_mode: pivotMode } : {}),
    pivot_edited: pivotEdited,
    ...(Array.isArray(matrix) && matrix.length === 16
      ? { pivot_content_matrix: matrix }
      : {}),
    ...(offset && (Number(offset.x) || Number(offset.y) || Number(offset.z))
      ? {
          pivot_offset: {
            x: Number(offset.x) || 0,
            y: Number(offset.y) || 0,
            z: Number(offset.z) || 0,
          },
        }
      : {}),
  };
}

function createMarkerAtObject(object) {
  object.updateWorldMatrix(true, false);
  const worldOrigin = new THREE.Vector3().setFromMatrixPosition(
    object.matrixWorld
  );
  const marker = createPivotMarker();
  marker.position.copy(worldOrigin);
  marker.scale.setScalar(computeMarkerScale(object));
  return marker;
}

/**
 * Start pivot edit: show marker and drive it with the main TransformControls
 * (or a temporary one if none is provided).
 */
export function startPivotEdit({
  scene,
  object,
  transformControls = null,
  camera = null,
  domElement = null,
  orbitControls = null,
} = {}) {
  if (!scene || !canEditPivot(object)) return null;

  endPivotSession({ restoreSelection: false });

  const marker = createMarkerAtObject(object);
  scene.add(marker);

  session = {
    scene,
    object,
    marker,
    transformControls: null,
    ownsControls: false,
    orbitControls: orbitControls || null,
    onDragging: null,
    savedShowSelectionBox: undefined,
    previousMode: 'translate',
    previousObject: object,
  };

  if (transformControls) {
    session.transformControls = transformControls;
    session.ownsControls = false;
    session.savedShowSelectionBox = transformControls.showSelectionBox;
    session.previousMode = transformControls.mode || 'translate';

    try {
      transformControls.detach();
    } catch (_) {
      /* ignore */
    }

    if ('showSelectionBox' in transformControls) {
      transformControls.showSelectionBox = false;
    }
    try {
      transformControls.setMode('translate');
      transformControls.setSpace?.('world');
    } catch (_) {
      /* ignore */
    }
    transformControls.attach(marker);

    const onDragging = (event) => {
      if (session?.orbitControls) {
        session.orbitControls.enabled = !event.value;
      }
    };
    transformControls.addEventListener('dragging-changed', onDragging);
    session.onDragging = onDragging;
  } else if (camera && domElement) {
    enablePivotDragging({ camera, domElement, orbitControls });
  }

  return session;
}

/** Attach a dedicated TransformControls to the marker (fallback path). */
export function enablePivotDragging({ camera, domElement, orbitControls }) {
  if (!session || !camera || !domElement || session.transformControls) return;

  const controls = new TransformControls(camera, domElement);
  controls.name = PIVOT_CONTROLS_NAME;
  controls.setMode('translate');
  try {
    controls.setSpace('world');
  } catch (_) {
    /* ignore */
  }
  if ('showSelectionBox' in controls) {
    controls.showSelectionBox = false;
  }
  controls.attach(session.marker);
  session.scene.add(controls);

  const onDragging = (event) => {
    if (orbitControls) orbitControls.enabled = !event.value;
  };
  controls.addEventListener('dragging-changed', onDragging);

  session.transformControls = controls;
  session.ownsControls = true;
  session.orbitControls = orbitControls || null;
  session.onDragging = onDragging;
}

function detachSessionControls() {
  if (!session?.transformControls) return;
  const { transformControls, onDragging, ownsControls, scene, orbitControls } =
    session;

  if (onDragging) {
    try {
      transformControls.removeEventListener('dragging-changed', onDragging);
    } catch (_) {
      /* ignore */
    }
  }
  if (orbitControls) orbitControls.enabled = true;

  try {
    transformControls.detach();
  } catch (_) {
    /* ignore */
  }

  if (ownsControls) {
    scene?.remove(transformControls);
    transformControls.dispose?.();
  } else if (session.savedShowSelectionBox !== undefined) {
    transformControls.showSelectionBox = session.savedShowSelectionBox;
  }

  session.transformControls = null;
  session.onDragging = null;
}

/**
 * Move the object's origin to a world-space point without moving its geometry.
 */
export function applyPivotToWorldPoint(object, targetWorld) {
  if (!object?.isObject3D || !targetWorld) return false;

  object.updateWorldMatrix(true, false);
  const mOld = object.matrixWorld.clone();

  const parent = object.parent;
  const newLocalPos = parent
    ? parent.worldToLocal(targetWorld.clone())
    : targetWorld.clone();
  object.position.copy(newLocalPos);
  object.updateMatrix();
  object.updateWorldMatrix(true, false);
  const mNew = object.matrixWorld.clone();

  const compensate = new THREE.Matrix4().copy(mNew).invert().multiply(mOld);

  if (object.isMesh && object.geometry) {
    object.geometry.applyMatrix4(compensate);
    object.geometry.computeBoundingBox?.();
    object.geometry.computeBoundingSphere?.();
  }

  object.children.slice().forEach((child) => {
    if (
      child.userData?.isHelper ||
      child.userData?.isPivotEditorMarker ||
      child.isHelper
    ) {
      return;
    }
    child.applyMatrix4(compensate);
  });

  // Accumulate content offset so planar assets (image/video/etc.) survive reload.
  const prevMatrixArr = object.userData?.pivotContentMatrix;
  const cumulative = compensate.clone();
  if (Array.isArray(prevMatrixArr) && prevMatrixArr.length === 16) {
    cumulative.multiply(new THREE.Matrix4().fromArray(prevMatrixArr));
  }

  // Prefer geometry-local content center for leaf meshes: matches fresh PlaneGeometry
  // / TextGeometry rebuilds better than decomposing the world compensate matrix.
  let pivotOffset = { x: 0, y: 0, z: 0 };
  if (object.isMesh && object.geometry?.boundingBox) {
    const c = object.geometry.boundingBox.getCenter(new THREE.Vector3());
    pivotOffset = { x: c.x, y: c.y, z: c.z };
  } else {
    const offsetPos = new THREE.Vector3();
    const offsetQuat = new THREE.Quaternion();
    const offsetScale = new THREE.Vector3();
    cumulative.decompose(offsetPos, offsetQuat, offsetScale);
    pivotOffset = { x: offsetPos.x, y: offsetPos.y, z: offsetPos.z };
  }

  object.updateMatrixWorld(true);
  ensureUserDataExtensible(object);
  setUserDataField(object, 'pivotContentMatrix', cumulative.toArray());
  setUserDataField(object, 'pivotOffset', pivotOffset);
  setUserDataField(object, 'pivotEdited', true);
  setUserDataField(object, 'native_pivot', true);
  setUserDataField(object, 'pivot_mode', 'file');
  setUserDataField(object, 'forceBoxUpdate', true);
  try {
    object.native_pivot = true;
    object.pivot_mode = 'file';
    object.pivotEdited = true;
  } catch (_) {
    try {
      Object.defineProperty(object, 'native_pivot', {
        value: true,
        writable: true,
        enumerable: true,
        configurable: true,
      });
      Object.defineProperty(object, 'pivotEdited', {
        value: true,
        writable: true,
        enumerable: true,
        configurable: true,
      });
    } catch (_) {
      /* flags also live on userData for publish */
    }
  }

  return true;
}

/**
 * World-space center of the object's visual content (bbox, helpers excluded).
 */
export function getObjectMeshCenterWorld(object) {
  if (!object?.isObject3D) return null;
  object.updateWorldMatrix(true, true);

  const box = new THREE.Box3();
  let hasContent = false;

  const expandFrom = (obj) => {
    if (
      !obj ||
      obj.userData?.isHelper ||
      obj.userData?.isPivotEditorMarker ||
      obj.isHelper ||
      obj.type === 'SkeletonHelper' ||
      obj.name === PIVOT_MARKER_NAME
    ) {
      return;
    }
    if (obj.isMesh || obj.isSkinnedMesh) {
      const meshBox = new THREE.Box3().setFromObject(obj);
      if (!meshBox.isEmpty()) {
        if (!hasContent) {
          box.copy(meshBox);
          hasContent = true;
        } else {
          box.union(meshBox);
        }
      }
    }
  };

  if (object.isMesh || object.isSkinnedMesh) {
    expandFrom(object);
  } else {
    object.traverse((child) => {
      if (child === object) return;
      expandFrom(child);
    });
  }

  if (!hasContent) {
    const fallback = new THREE.Box3().setFromObject(object);
    if (fallback.isEmpty()) return null;
    return fallback.getCenter(new THREE.Vector3());
  }
  return box.getCenter(new THREE.Vector3());
}

/**
 * Instantly move the transform origin to the mesh bounding-box center
 * (geometry stays put in the world). Same persistence as Edit Pivot Point.
 */
export function movePivotToMeshCenter(object, options = {}) {
  if (!canEditPivot(object)) return false;

  const centerWorld = getObjectMeshCenterWorld(object);
  if (!centerWorld) return false;

  // Already at center — still mark so publish keeps file-pivot identity.
  const originWorld = object.getWorldPosition(new THREE.Vector3());
  if (originWorld.distanceToSquared(centerWorld) < 1e-12) {
    ensureUserDataExtensible(object);
    setUserDataField(object, 'pivotEdited', true);
    setUserDataField(object, 'native_pivot', true);
    setUserDataField(object, 'pivot_mode', 'file');
    try {
      object.pivotEdited = true;
      object.native_pivot = true;
      object.pivot_mode = 'file';
    } catch (_) {
      /* ignore */
    }
  } else if (!applyPivotToWorldPoint(object, centerWorld)) {
    return false;
  }

  const pivotFields = getPivotPublishFields(object);
  const projectId =
    options.projectId ||
    (typeof window !== 'undefined'
      ? window.location?.pathname?.split('/')?.pop()
      : null);
  cachePivotRestoreData(
    projectId,
    [
      object.name,
      object._id,
      object.url,
      object.temp_file_name,
      object.file_name,
      object.userData?.text_material?.id,
    ],
    pivotFields
  );
  return true;
}

/**
 * Move the object's origin to the marker without moving its geometry.
 */
export function applyPivotFromSession() {
  if (!session) return false;
  const { object, marker } = session;
  if (!object || !marker) return false;

  const targetWorld = marker.getWorldPosition(new THREE.Vector3());
  const ok = applyPivotToWorldPoint(object, targetWorld);
  if (ok) {
    marker.position.copy(targetWorld);
  }
  return ok;
}

/** Apply pivot change, tear down session, re-attach TransformControls to object. */
export function confirmPivotEdit(options = {}) {
  if (!session) return false;
  const object = session.object;
  const transformControls = session.ownsControls
    ? null
    : session.transformControls;
  const previousMode = session.previousMode || 'translate';

  const ok = applyPivotFromSession();
  if (ok && object) {
    const pivotFields = getPivotPublishFields(object);
    const projectId =
      options.projectId ||
      (typeof window !== 'undefined'
        ? window.location?.pathname?.split('/')?.pop()
        : null);
    cachePivotRestoreData(
      projectId,
      [
        object.name,
        object._id,
        object.url,
        object.temp_file_name,
        object.file_name,
        object.userData?.text_material?.id,
      ],
      pivotFields
    );
  }
  endPivotSession({ restoreSelection: false });

  if (ok && transformControls && object) {
    try {
      transformControls.setMode(previousMode);
    } catch (_) {
      /* ignore */
    }
    transformControls.attach(object);
  }
  return ok;
}

/** Cancel without changing the object; restore TransformControls. */
export function cancelPivotEdit() {
  if (!session) return;
  const object = session.previousObject || session.object;
  const transformControls = session.ownsControls
    ? null
    : session.transformControls;
  const previousMode = session.previousMode || 'translate';

  endPivotSession({ restoreSelection: false });

  if (transformControls && object) {
    try {
      transformControls.setMode(previousMode);
    } catch (_) {
      /* ignore */
    }
    transformControls.attach(object);
  }
}

/** Remove marker + controls and clear the session. */
export function endPivotSession({ restoreSelection = true } = {}) {
  if (!session) return;

  const object = session.previousObject || session.object;
  const transformControls = session.ownsControls
    ? null
    : session.transformControls;
  const previousMode = session.previousMode || 'translate';

  detachSessionControls();

  if (session.marker && session.scene) {
    session.scene.remove(session.marker);
    disposeObject3D(session.marker);
  }

  session = null;

  if (restoreSelection && transformControls && object) {
    try {
      transformControls.setMode(previousMode);
    } catch (_) {
      /* ignore */
    }
    transformControls.attach(object);
  }
}

export function getPivotSessionObject() {
  return session?.object || null;
}

export function hasActivePivotSession() {
  return !!session;
}
/** @deprecated use startPivotEdit */
export function beginPivotSession({ scene, object }) {
  return startPivotEdit({ scene, object });
}

/** @deprecated use cancelPivotEdit / confirmPivotEdit */
export function disablePivotDragging() {
  detachSessionControls();
}

